export type Category = "cosmico" | "elemento" | "flora" | "fauna";
export type Rarity = "comun" | "raro" | "legendario";

export interface Ingredient {
    id: string;
    name: string;
    glyph: string;
    color: string;
    category: Category;
}

export interface Recipe {
    id: string;
    ingredients: string[];
    name: string;
    effect: string;
    description: string;
    rarity: Rarity;
}

export const INGREDIENTS: Ingredient[] = [
    { id: "luna", name: "Luna", glyph: "☾", color: "#cbd5f5", category: "cosmico" },
    { id: "sol", name: "Sol", glyph: "☀", color: "#fbbf24", category: "cosmico" },
    { id: "estrella", name: "Estrella", glyph: "✦", color: "#fde68a", category: "cosmico" },
    { id: "cometa", name: "Cometa", glyph: "✺", color: "#c4b5fd", category: "cosmico" },

    { id: "agua", name: "Agua", glyph: "≈", color: "#38bdf8", category: "elemento" },
    { id: "brasa", name: "Brasa", glyph: "✸", color: "#f97316", category: "elemento" },
    { id: "rayo", name: "Rayo", glyph: "ϟ", color: "#eab308", category: "elemento" },
    { id: "hielo", name: "Hielo", glyph: "❆", color: "#7dd3fc", category: "elemento" },

    { id: "hongo", name: "Hongo", glyph: "✿", color: "#a78bfa", category: "flora" },
    { id: "petalo", name: "Pétalo", glyph: "❀", color: "#f472b6", category: "flora" },
    { id: "raiz", name: "Raíz", glyph: "⌇", color: "#65a30d", category: "flora" },
    { id: "espina", name: "Espina", glyph: "✤", color: "#7c2d12", category: "flora" },

    { id: "ojo", name: "Ojo", glyph: "◉", color: "#22c55e", category: "fauna" },
    { id: "hueso", name: "Hueso", glyph: "✷", color: "#e7e5e4", category: "fauna" },
    { id: "escama", name: "Escama", glyph: "◈", color: "#14b8a6", category: "fauna" },
    { id: "pluma", name: "Pluma", glyph: "❥", color: "#fda4af", category: "fauna" },
];

export const RECIPES: Recipe[] = [
    { id: "eclipse", ingredients: ["luna", "hueso"], name: "Eclipse", effect: "eclipse", description: "Invierte la realidad.", rarity: "comun" },
    { id: "vapor", ingredients: ["sol", "agua"], name: "Vapor", effect: "vapor", description: "Niebla densa, vista borrosa.", rarity: "comun" },
    { id: "pyromancia", ingredients: ["brasa", "raiz"], name: "Pyromancia", effect: "pyromancia", description: "El mundo crepita en rojo.", rarity: "comun" },
    { id: "iris", ingredients: ["petalo", "rayo"], name: "Iris", effect: "iris", description: "Tormenta de color.", rarity: "comun" },
    { id: "vision", ingredients: ["hongo", "ojo"], name: "Visión", effect: "vision", description: "Ves de más.", rarity: "comun" },
    { id: "levitation", ingredients: ["agua", "hongo"], name: "Levitación", effect: "levitation", description: "La página flota.", rarity: "comun" },
    { id: "glitch", ingredients: ["hueso", "rayo"], name: "Glitch", effect: "glitch", description: "Datos corruptos.", rarity: "comun" },
    { id: "crepusculo", ingredients: ["sol", "luna"], name: "Crepúsculo", effect: "crepusculo", description: "Cae la noche.", rarity: "comun" },
    { id: "escarcha", ingredients: ["hielo", "pluma"], name: "Escarcha", effect: "escarcha", description: "Aire frío y quieto.", rarity: "comun" },
    { id: "velocidad", ingredients: ["cometa", "rayo"], name: "Velocidad", effect: "velocidad", description: "Todo gira un instante.", rarity: "comun" },
    { id: "aurora", ingredients: ["estrella", "petalo"], name: "Aurora", effect: "aurora", description: "Luces danzando en el cielo.", rarity: "comun" },
    { id: "espejo", ingredients: ["agua", "escama"], name: "Espejo", effect: "espejo", description: "Reflejo invertido.", rarity: "comun" },
    { id: "fantasma", ingredients: ["espina", "hueso"], name: "Fantasma", effect: "fantasma", description: "La página se desvanece.", rarity: "comun" },
    { id: "tormenta", ingredients: ["brasa", "hielo"], name: "Tormenta", effect: "tormenta", description: "Frío y calor a la vez.", rarity: "comun" },
    { id: "levedad", ingredients: ["ojo", "pluma"], name: "Levedad", effect: "levedad", description: "Pesa menos.", rarity: "comun" },
    { id: "gravedad", ingredients: ["raiz", "hielo"], name: "Gravedad", effect: "gravedad", description: "El mundo tira hacia abajo.", rarity: "comun" },
    { id: "matrix", ingredients: ["estrella", "ojo"], name: "Códice", effect: "matrix", description: "Texto en cascada verde.", rarity: "comun" },

    { id: "cosmos", ingredients: ["luna", "sol", "estrella"], name: "Cosmos", effect: "cosmos", description: "Una rotación celeste completa.", rarity: "raro" },
    { id: "cataclismo", ingredients: ["brasa", "rayo", "espina"], name: "Cataclismo", effect: "cataclismo", description: "La tierra tiembla.", rarity: "raro" },
    { id: "sueno", ingredients: ["agua", "petalo", "pluma"], name: "Sueño", effect: "sueno", description: "Realidad blanda.", rarity: "raro" },

    { id: "equilibrio", ingredients: ["luna", "sol", "agua", "brasa"], name: "Equilibrio", effect: "equilibrio", description: "Los cuatro elementos en pulso.", rarity: "legendario" },
    { id: "necromancia", ingredients: ["hueso", "ojo", "espina", "escama"], name: "Necromancia", effect: "necromancia", description: "El velo se rompe.", rarity: "legendario" },
];

export const ALL_POTION_CLASSES = [
    "potion-active",
    "potion-fizzle",
    ...RECIPES.map((r) => `potion-${r.effect}`),
];

export function normalizeKey(ids: string[]): string {
    return [...ids].sort().join(",");
}

const RECIPE_INDEX: Map<string, Recipe> = new Map(
    RECIPES.map((r) => [normalizeKey(r.ingredients), r]),
);

export function findRecipe(ids: string[]): Recipe | null {
    if (ids.length < 2 || ids.length > 4) return null;
    return RECIPE_INDEX.get(normalizeKey(ids)) ?? null;
}

export function getIngredient(id: string): Ingredient | undefined {
    return INGREDIENTS.find((i) => i.id === id);
}

export function getRecipe(id: string): Recipe | undefined {
    return RECIPES.find((r) => r.id === id);
}

export const CATEGORIES: { id: Category; label: string }[] = [
    { id: "cosmico", label: "Cósmico" },
    { id: "elemento", label: "Elemento" },
    { id: "flora", label: "Flora" },
    { id: "fauna", label: "Fauna" },
];

export const RARITY_LABEL: Record<Rarity, string> = {
    comun: "común",
    raro: "raro",
    legendario: "legendario",
};

export const RARITY_DURATION_MS: Record<Rarity, number> = {
    comun: 4600,
    raro: 5800,
    legendario: 7200,
};

export interface Achievement {
    id: string;
    label: string;
    threshold: number;
    description: string;
}

export const ACHIEVEMENTS: Achievement[] = [
    { id: "aprendiz", label: "Aprendiz", threshold: 5, description: "Cinco recetas descubiertas." },
    { id: "boticario", label: "Boticario", threshold: 10, description: "Diez recetas descubiertas." },
    {
        id: "archimago",
        label: "Archimago",
        threshold: RECIPES.length,
        description: "Todas las recetas descubiertas.",
    },
];

export function getRecipeCategories(recipe: Recipe): Category[] {
    const cats = new Set<Category>();
    for (const id of recipe.ingredients) {
        const ing = getIngredient(id);
        if (ing) cats.add(ing.category);
    }
    return [...cats].sort();
}
